import type { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import type { RouteLocationNormalized } from 'vue-router';
import {
  deleteProfileImage,
  deleteSelf,
  getUserProfile,
  invitationLogin,
  login,
  logout,
  patchUser,
  refreshToken,
  resendMfaToken,
  updateProfileImage,
  verifyCredentials,
  verifyMfaToken,
} from '@/api/auth';
import { loadLocaleMessages, setLocaleInLocalStorage } from '@/plugins/i18n';
import {
  type InvitationLoginPayload,
  type LoginPayload,
  REFRESH_TOKEN,
  type UserPatchPayload,
  type UserProfile,
} from '@/types';
import {
  clearTokens,
  getCorrelationId,
  removeCorrelationId,
  setAccessToken,
  setCorrelationId,
  setRefreshToken,
} from '@/utils/helpers';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    loggedIn: false,
    path: '',
    profile: {} as UserProfile,
  }),
  getters: {
    userId: (state) => state.profile.id,
    isUserAdmin: (state) => !!state.profile.is_admin,
    isUserTeamlead: (state) => !!state.profile.is_teamlead,
    hasPeopleAccess: (state) => !!state.profile.has_people_access,
    loginRedirectPath: (state) => (currentRoute: RouteLocationNormalized) => {
      return state.path || currentRoute.query?.redirect || '/';
    },
    appAccess: (state) => state.profile.app_access,
    isUserStaff: (state) => !!state.profile.is_staff,
    isStaffMode: (state) => state.loggedIn && !!state.profile.staff_mode,
  },
  actions: {
    async postLogin(payload: LoginPayload) {
      const { data, error, response } = await login(payload);

      if (!error) {
        setAccessToken(data.access);
        setRefreshToken(data.refresh);
      }

      return { response, error };
    },
    async credentialsLoginStep(payload: LoginPayload) {
      const { response, error } = await verifyCredentials(payload);

      if (!error) {
        setCorrelationId(response.headers['x-correlation-id']);
      }

      return { response, error };
    },
    async logout() {
      this.loggedIn = false;
      const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN);
      clearTokens();
      await logout(refreshTokenValue);
    },
    async refreshToken() {
      const refreshBody = { refresh: localStorage.getItem(REFRESH_TOKEN) || '' };
      try {
        const { data } = await refreshToken(refreshBody);
        setAccessToken(data.access);
        if (data.refresh) {
          setRefreshToken(data.refresh);
        }
        return Promise.resolve(data.access);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async setUserProfileAndLoggedInState() {
      const { data, error } = await getUserProfile();

      if (!error) {
        this.profile = data;
        this.loggedIn = true;
      }
    },
    async verifyMfaToken(payload: { token: string }) {
      try {
        const { data } = await verifyMfaToken(payload, getCorrelationId());

        removeCorrelationId();

        setAccessToken(data.access_token);
        setRefreshToken(data.refresh_token);

        return { data, error: null };
      } catch (e) {
        const error = e as AxiosError;

        return { data: null, error };
      }
    },
    async resendMfaToken() {
      try {
        const response = await resendMfaToken(null, getCorrelationId());

        setCorrelationId(response.headers['x-correlation-id']);

        return { error: null };
      } catch (e) {
        const error = e as AxiosError;

        return { error };
      }
    },
    async inviteLogin(invitationId: string, payload: InvitationLoginPayload) {
      const { data, error } = await invitationLogin(invitationId, payload);

      if (!error && data.access_token && data.refresh_token) {
        setAccessToken(data.access_token);
        setRefreshToken(data.refresh_token);
        if (payload.locale) {
          setLocaleInLocalStorage(payload.locale);
          loadLocaleMessages(payload.locale);
        }
      }

      return { data, error };
    },
    async updateProfileImage(image: File) {
      const { error } = await updateProfileImage(this.profile.id, image);
      if (!error) {
        await this.setUserProfileAndLoggedInState();
      }
    },
    async deleteProfileImage() {
      const { error } = await deleteProfileImage(this.profile.id);
      if (!error) {
        await this.setUserProfileAndLoggedInState();
      }
    },
    async deleteMyAccount(password: string) {
      const { error } = await deleteSelf({ email: this.profile.email, password });

      if (!error) {
        await this.logout();
      }

      return { error: error ?? null };
    },
    async changeProfileInfo(data: UserPatchPayload) {
      const { error, response } = await patchUser(this.profile.id, data);
      if (!error) {
        if (data.locale) {
          setLocaleInLocalStorage(data.locale);
          loadLocaleMessages(data.locale);
        }

        this.profile = { ...this.profile, ...response.data };
      }
    },
  },
});
